// Updated TelegramInputBar with first-record playback fix and UI adjustment
import React, { useRef, useState, useEffect } from "react";
import { IconButton, InputBase } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import EmojiPicker from "emoji-picker-react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

export const TelegramInputBar = ({
                                     onSend,
                                     onFileSelect,
                                     onImageSelect,
                                     onEmojiSelect,
                                     onRecordAudio,
                                 }) => {
    const [message, setMessage] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [recordingState, setRecordingState] = useState("idle");
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [audioUrl, setAudioUrl] = useState(null);
    const [startCoords, setStartCoords] = useState(null);
    const [recordingStartTime, setRecordingStartTime] = useState(null);
    const [recordDuration, setRecordDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const emojiRef = useRef(null);
    const timerRef = useRef(null);
    const audioRef = useRef(new Audio());

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiRef.current && !emojiRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showEmojiPicker]);


    const getSupportedMimeType = () => {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
        if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
        if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
        return "";
    };

    const formatDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = getSupportedMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
            setAudioChunks([]);

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) setAudioChunks((prev) => [...prev, e.data]);
            };

            recorder.onstop = () => {
                const blob = new Blob(audioChunks, { type: recorder.mimeType });
                const url = URL.createObjectURL(blob);
                audioRef.current.src = url; // Set first!
                setAudioUrl(url);
                if (recordingState === "locked" && blob.size > 0) onRecordAudio(blob);
            };

            recorder.start();
            setMediaRecorder(recorder);
            const start = Date.now();
            setRecordingStartTime(start);
            setRecordDuration(0);
            setRecordingState("recording");
            timerRef.current = setInterval(() => {
                const now = Date.now();
                setRecordDuration(Math.floor((now - start) / 1000));
            }, 1000);
        } catch (err) {
            console.error("Microphone error:", err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== "inactive") mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingState("locked");
    };

    const cancelRecording = () => {
        setAudioChunks([]);
        setAudioUrl(null);
        if (mediaRecorder && mediaRecorder.state !== "inactive") mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingState("cancelled");
        setTimeout(() => setRecordingState("idle"), 300);
    };

    const playPauseAudio = () => {
        try {
            if (!audioRef.current || !audioRef.current.src) return;
            if (audioRef.current.paused) {
                audioRef.current.play().catch((err) => {
                    console.error("Playback error:", err);
                });
            } else {
                audioRef.current.pause();
            }
        } catch (err) {
            console.error("Unhandled audio play/pause error:", err);
        }
    };


    useEffect(() => {
        const audio = audioRef.current;
        const handleEnded = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        if (audio) {
            audio.addEventListener("ended", handleEnded);
            audio.addEventListener("play", handlePlay);
            audio.addEventListener("pause", handlePause);
        }
        return () => {
            if (audio) {
                audio.removeEventListener("ended", handleEnded);
                audio.removeEventListener("play", handlePlay);
                audio.removeEventListener("pause", handlePause);
            }
        };
    }, [audioUrl]);

    useEffect(() => {
        const handlePointerMove = (e) => {
            if (recordingState !== "recording" || !startCoords) return;
            const dx = e.clientX - startCoords.x;
            const dy = e.clientY - startCoords.y;
            if (dy < -60) setRecordingState("locked");
            if (dx < -100) cancelRecording();
        };
        const handlePointerUp = () => {
            if (recordingState === "recording") stopRecording();
        };
        if (recordingState === "recording") {
            document.addEventListener("pointermove", handlePointerMove);
            document.addEventListener("pointerup", handlePointerUp);
        }
        return () => {
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
        };
    }, [recordingState, startCoords]);

    const handleSend = () => {
        if (message.trim()) {
            onSend(message);
            setMessage("");
        } else if (audioUrl) {
            const audio = new Blob(audioChunks);
            onRecordAudio(audio);
            setAudioUrl(null);
            setRecordingState("idle");
        }
    };

    return (
        <div style={{ position: "relative"}}>
            {showEmojiPicker && (
                <div ref={emojiRef} style={{ position: "absolute", bottom: 56, left: 12, zIndex: 200 }}>
                    <EmojiPicker onEmojiClick={(e) => setMessage((prev) => prev + e.emoji)} theme="light" />
                </div>
            )}
            <div style={{ display: "flex", alignItems: "center", padding: 12, gap: 8 }}>
                <div style={{ flexGrow: 1, background: "#ffffff",    boxShadow: "0px -2px 6px rgba(0, 0, 0, 0.1)", // ✅ subtle top shadow
                    backgroundColor: "#fff", // optional for consistent appearance
                    zIndex: 20,     // ✅ subtle top shadow
                    borderRadius: 20, padding: 8, display: "flex", alignItems: "center" }}>
                    <IconButton onClick={() => fileInputRef.current.click()} size="large"><AttachFileIcon /></IconButton>
                    <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => onFileSelect(e.target.files[0])} />

                    {audioUrl && recordingState === "locked" ? (
                        <>
                            <IconButton onClick={playPauseAudio} size="large">
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            <IconButton
                                onClick={() => {
                                    setAudioUrl(null);
                                    setRecordingState("idle");
                                }}
                                size="large"><DeleteForeverIcon /></IconButton>
                            <IconButton onClick={handleSend} size="large"><SendIcon /></IconButton>
                        </>
                    ) : message.trim() ? (
                        <IconButton onClick={handleSend} size="large"><SendIcon /></IconButton>
                    ) : (
                        <IconButton
                            onPointerDown={(e) => {
                                setStartCoords({ x: e.clientX, y: e.clientY });
                                startRecording();
                            }}
                            style={{ animation: recordingState === "recording" ? "pulse 1s infinite" : "none" }}
                            size="large">
                            <MicIcon color={recordingState !== "idle" ? "error" : "inherit"} />
                        </IconButton>
                    )}

                    <InputBase
                        placeholder="پیام خود را بنویسید..."
                        multiline
                        fullWidth
                        value={recordingState === "recording" ? formatDuration(recordDuration) : message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)} size="large"><EmojiEmotionsIcon /></IconButton>
                </div>
            </div>
            <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
        </div>
    );
};
